import {Controller} from "@hotwired/stimulus"

// Shift+H → Home; Shift+D → Download dropdown; Shift+P → Page dropdown; Shift+S → Story dropdown.
// With the Page/Story dropdown open, a bare 1..9 jumps to that item; with Download open, Enter starts it.
// Esc closes any nav dropdown.
// Shift+1..9 (dropdown closed) still jumps to a page directly; Ctrl/Cmd+Shift+1..9 to a story.
// Holding Shift swaps page icons for their shortcut number.
// ponytail: uses event.code (layout-independent) instead of @github/hotkey,
// which matches event.key so Shift+1 must be declared as "!" and breaks on non-US layouts.
export default class extends Controller {
  connect() {
    this.onKeydown = this.keydown.bind(this)
    this.onKeyup = this.keyup.bind(this)
    this.onBlur = () => this.toggleNumbers(false)
    window.addEventListener("keydown", this.onKeydown)
    window.addEventListener("keyup", this.onKeyup)
    window.addEventListener("blur", this.onBlur)
  }

  disconnect() {
    window.removeEventListener("keydown", this.onKeydown)
    window.removeEventListener("keyup", this.onKeyup)
    window.removeEventListener("blur", this.onBlur)
  }

  keydown(event) {
    if (event.key === "Escape") return this.closeDropdowns()
    // Enter while the Download dropdown is open → start the download.
    if (event.key === "Enter" && this.expanded("[data-shortcut-download]")) {
      const submit = document.querySelector("[data-shortcut-download-submit]")
      if (submit) { event.preventDefault(); submit.click() }
      return
    }
    if (event.target.closest("input, textarea, select, [contenteditable]")) return
    if (event.key === "Shift") this.toggleNumbers(true)

    // With a page/story dropdown open, a bare 1..9 picks that numbered item.
    const pagesOpen = this.expanded("[data-shortcut-pages]")
    const storiesOpen = this.expanded("[data-shortcut-stories]")
    if (pagesOpen || storiesOpen) {
      const digit = event.code.match(/^(?:Digit|Numpad)([1-9])$/)
      if (digit) {
        const link = document.querySelectorAll(pagesOpen ? "[data-shortcut-page]" : "[data-shortcut-story]")[parseInt(digit[1], 10) - 1]
        if (!link) return
        event.preventDefault()
        link.click()
        return
      }
    }

    if (!event.shiftKey) return

    // Shift+P / Shift+S open the page / story dropdown (Esc closes it).
    if (event.code === "KeyP" || event.code === "KeyS") {
      const btn = document.querySelector(event.code === "KeyP" ? "[data-shortcut-pages]" : "[data-shortcut-stories]")
      if (!btn) return
      event.preventDefault()
      if (btn.getAttribute("aria-expanded") !== "true") btn.click()
      return
    }
    if (event.code === "KeyH" || event.code === "KeyD") {
      const target = document.querySelector(event.code === "KeyH" ? "[data-shortcut-home]" : "[data-shortcut-download]")
      if (!target) return
      event.preventDefault()
      target.click()
      return
    }
    const match = event.code.match(/^(?:Digit|Numpad)([1-9])$/)
    if (!match) return
    const selector = (event.ctrlKey || event.metaKey) ? "[data-shortcut-story]" : "[data-shortcut-page]"
    const link = document.querySelectorAll(selector)[parseInt(match[1], 10) - 1]
    if (!link) return
    event.preventDefault()
    link.click()
  }

  keyup(event) {
    if (event.key === "Shift") this.toggleNumbers(false)
  }

  expanded(selector) {
    const el = document.querySelector(selector)
    return !!el && el.getAttribute("aria-expanded") === "true"
  }

  // Esc: hide whichever nav dropdown (page/story/download) is open, via its dropdown controller.
  closeDropdowns() {
    document.querySelectorAll("[data-shortcut-pages], [data-shortcut-stories], [data-shortcut-download]").forEach(btn => {
      if (btn.getAttribute("aria-expanded") !== "true") return
      const el = btn.closest('[data-controller~="dropdown"]')
      if (el) this.application.getControllerForElementAndIdentifier(el, "dropdown")?.hide(null)
    })
  }

  toggleNumbers(show) {
    this.element.querySelectorAll("[data-shortcut-icon]").forEach(el => el.classList.toggle("hidden", show))
    this.element.querySelectorAll("[data-shortcut-num]").forEach(el => el.classList.toggle("hidden", !show))
    // hints live outside the nav (header Home/Download), so search document-wide
    document.querySelectorAll("[data-shortcut-hint]").forEach(el => el.classList.toggle("hidden", !show))
  }
}
